import { UPLOAD_SECTION_IDS, type UploadStep, type UploadStepState } from "./uploadStepStates";

interface Props {
  steps: UploadStep[];
  canWrite: boolean;
}

const STEP_CLASSES: Record<UploadStepState, string> = {
  done: "border-transparent",
  current: "border-rs-green/35 bg-rs-green/10",
  todo: "border-transparent",
};

const MARKER_CLASSES: Record<UploadStepState, string> = {
  done: "border-rs-green bg-rs-green text-rs-black",
  current: "border-rs-green text-rs-green",
  todo: "border-[#3c4235] text-rs-light-gray",
};

const GROUP_LABEL_CLASSES =
  "mx-2.5 mb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-rs-light-gray/70 max-[1000px]:hidden";

function scrollToSection(sectionId: string) {
  document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export function UploadSteps({ steps, canWrite }: Props) {
  const extraLinks = [
    ...(canWrite ? [{ sectionId: UPLOAD_SECTION_IDS.tds, label: "Upload TDS files", icon: "description" }] : []),
    { sectionId: UPLOAD_SECTION_IDS.rowGuide, label: "How a row is read", icon: "menu_book" },
  ];

  return (
    <nav
      aria-label="Upload steps"
      className="sticky top-[calc(var(--page-header-h,112px)+24px)] self-start flex flex-col gap-1 max-[1000px]:static"
    >
      <p className={GROUP_LABEL_CLASSES}>Upload steps</p>
      <ol className="m-0 p-0 list-none flex flex-col gap-1 max-[1000px]:flex-row max-[1000px]:overflow-x-auto">
        {steps.map((step, stepIndex) => (
          <li key={step.title} className="max-[1000px]:flex-none">
            <button
              type="button"
              aria-current={step.state === "current" ? "step" : undefined}
              className={`grid w-full grid-cols-[24px_minmax(0,1fr)] gap-x-2.5 gap-y-px rounded-lg border px-2.5 py-2.25 text-left cursor-pointer bg-transparent hover:bg-rs-off-black max-[1000px]:grid-cols-[24px_auto] max-[1000px]:items-center ${STEP_CLASSES[step.state]}`}
              onClick={() => scrollToSection(step.sectionId)}
            >
              <span
                className={`row-span-2 grid size-6 place-items-center rounded-full border text-xs font-bold max-[1000px]:row-span-1 ${MARKER_CLASSES[step.state]}`}
                aria-label={step.state === "done" ? "Done" : undefined}
              >
                {step.state === "done" ? "✓" : stepIndex + 1}
              </span>
              <b className="text-[13.5px] font-semibold text-white">{step.title}</b>
              <span className="text-xs leading-snug text-rs-light-gray max-[1000px]:hidden">{step.description}</span>
            </button>
          </li>
        ))}
      </ol>
      <p className={`${GROUP_LABEL_CLASSES} mt-3.5`}>Also on this tab</p>
      {extraLinks.map((link) => (
        <button
          key={link.sectionId}
          type="button"
          className="flex w-full items-center gap-2.5 rounded-lg border-0 bg-transparent px-2.5 py-2 text-left text-[13.5px] text-rs-light-gray cursor-pointer hover:bg-rs-off-black hover:text-white max-[1000px]:hidden"
          onClick={() => scrollToSection(link.sectionId)}
        >
          <span className="material-symbols-sharp text-[18px] text-rs-light-gray/60" aria-hidden="true">
            {link.icon}
          </span>
          {link.label}
        </button>
      ))}
    </nav>
  );
}
